import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod/v4";
import { ConversationPlatform } from "../../conversation";
import { ConversationResolutionSentiment } from "../../conversation-insights";
import { DateSchema } from "../../conversation/shared-schemas";
import {
  CsatSurveyMethod,
  SurveySendTimeOptions,
} from "../../support/csat-survey";
import { StepType } from "../common";
import { CONVERSATION_STATUSES_V3, TimeUnit } from "../rules-v3/types";
import {
  AssignV3Strategy,
  SetStatusActionStatus,
  SetStatusSnoozePeriod,
} from "./actions";
import {
  DateQueryOperator,
  dateQueryOperators,
  ErrorHandlingStrategy,
  QueryOperator,
  RuleActionType,
  RuleConditionType,
  RuleTriggerType,
  selectionQueryOperators,
  textQueryOperators,
} from "./types";

export enum AssignmentTypeOption {
  AI_AGENT_ASSIGNEE = "AI agent assigned",
  SUPPORT_AGENT_ASSIGNEE = "Support agent assigned",
  NO_ASSIGNEE = "No assignee",
}

export enum TicketStatusOption {
  OPEN = "open",
  CLOSED = "closed",
  SNOOZED = "snoozed",
  IN_PROGRESS = "in_progress",
}

export enum SubjectLineMatchType {
  EXACT = "exact",
  CONTAINS = "contains",
}

// Base step schema
const baseStepSchema = z.object({
  customTitle: z.string().optional(),
  type: createEnum(StepType),
  id: z.string(),
});

/* Trigger Metadata Schemas */

/**
 * The actor that fired the trigger, when one can be identified. Currently
 * populated by message-related triggers from the message's `senderType` /
 * `senderMongoUserId`. `null` / omitted for triggers that have no meaningful
 * actor (tag added, status changed by another rule, etc.).
 */
export const triggerActorSchema = z
  .object({ assigneeType: z.enum(["USER", "AI"]), mongoUserId: z.string() })
  .nullish();

export type TriggerActor = z.infer<typeof triggerActorSchema>;

export const triggerMetadataSchema = z.object({
  triggerType: createEnum(RuleTriggerType),
  triggeredAt: DateSchema,
  subjectEntityId: z.string(),
  triggerDetails: z.record(z.string(), z.unknown()),
  actor: triggerActorSchema,
});

export type TriggerMetadata = z.infer<typeof triggerMetadataSchema>;

/* Condition Group Schemas for AND/OR Logic */

const conditionGroupOperator = z.enum(["AND", "OR"]);

export type ConditionGroupOperator = z.infer<typeof conditionGroupOperator>;

/* Rule Condition Schemas */

export const ticketChannelConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_CHANNEL),
  channels: z.array(createEnum(ConversationPlatform)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

export type TicketChannelCondition = z.infer<
  typeof ticketChannelConditionSchema
>;

export const topicConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TOPIC),
  topics: z.array(z.string()).optional(),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

export type TopicCondition = z.infer<typeof topicConditionSchema>;

export const sentFromEmailConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.SENT_FROM_EMAIL),
  emails: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});

export type SentFromEmailCondition = z.infer<
  typeof sentFromEmailConditionSchema
>;

export const receivedAtEmailConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.RECEIVED_AT_EMAIL),
  emails: z.array(z.string()).optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.IS_EXACTLY),
});

export type ReceivedAtEmailCondition = z.infer<
  typeof receivedAtEmailConditionSchema
>;

export const subjectLineConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.SUBJECT_LINE),
  fuzzy_match: z.string().optional(),
  queryStrings: z.array(z.string()).optional(),
  caseSensitive: z.boolean().optional(),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});

export type SubjectLineCondition = z.infer<typeof subjectLineConditionSchema>;

export const messageBodyMatchesConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.MESSAGE_BODY_MATCHES),
  queryString: z.string().optional(),
  queryStrings: z.array(z.string()).optional(),
  caseSensitive: z.boolean().default(false),
  queryOperator: z.enum(textQueryOperators).default(QueryOperator.CONTAINS),
});

export type MessageBodyMatchesCondition = z.infer<
  typeof messageBodyMatchesConditionSchema
>;

export const afterHoursConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.AFTER_HOURS),
});

export type AfterHoursCondition = z.infer<typeof afterHoursConditionSchema>;

export const assignmentTypeConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.ASSIGNMENT_TYPE),
  assignmentType: createEnum(AssignmentTypeOption).default(
    AssignmentTypeOption.SUPPORT_AGENT_ASSIGNEE,
  ),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

export type AssignmentTypeCondition = z.infer<
  typeof assignmentTypeConditionSchema
>;

export const lastOrderTimeConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.LAST_ORDER_TIME),
  queryOperator: z
    .enum(dateQueryOperators)
    .default(DateQueryOperator.WITHIN_LAST),
  days: z.number().optional(),
  date: z.string().optional(),
  startDate: z.string().optional(),
  endDate: z.string().optional(),
});

export type LastOrderTimeCondition = z.infer<
  typeof lastOrderTimeConditionSchema
>;

export const messageSentimentConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.MESSAGE_SENTIMENT),
  sentiments: z.array(createEnum(ConversationResolutionSentiment)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

export type MessageSentimentCondition = z.infer<
  typeof messageSentimentConditionSchema
>;

export const ticketStatusConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.TICKET_STATUS),
  statuses: z.array(createEnum(TicketStatusOption)),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

export type TicketStatusCondition = z.infer<typeof ticketStatusConditionSchema>;

export const hasTagConditionSchema = z.object({
  conditionType: z.literal(RuleConditionType.HAS_TAG),
  tags: z.array(z.string()).optional(),
  queryOperator: z.enum(selectionQueryOperators).default(QueryOperator.IS),
});

export type HasTagCondition = z.infer<typeof hasTagConditionSchema>;

export const ruleConditionSchema = z.union([
  ticketChannelConditionSchema,
  ticketStatusConditionSchema,
  topicConditionSchema,
  sentFromEmailConditionSchema,
  receivedAtEmailConditionSchema,
  subjectLineConditionSchema,
  messageBodyMatchesConditionSchema,
  afterHoursConditionSchema,
  assignmentTypeConditionSchema,
  lastOrderTimeConditionSchema,
  messageSentimentConditionSchema,
  hasTagConditionSchema,
]);

export type RuleCondition = z.infer<typeof ruleConditionSchema>;

const conditionGroupSchema_: z.ZodType<ConditionGroup> = z.lazy(() =>
  z.object({
    operator: conditionGroupOperator,
    conditions: z.array(z.union([ruleConditionSchema, conditionGroupSchema_])),
  }),
);

export const conditionGroupSchema: z.ZodType<ConditionGroup> =
  conditionGroupSchema_;

export interface ConditionGroup {
  operator: ConditionGroupOperator;
  conditions: (RuleCondition | ConditionGroup)[];
}

/* Rule Action Schemas */

export const setStatusActionSchema = z.object({
  actionType: z.literal(RuleActionType.SET_STATUS),
  status: createEnum(SetStatusActionStatus),
  snoozePeriod: createEnum(SetStatusSnoozePeriod).optional(),
});

export type SetStatusAction = z.infer<typeof setStatusActionSchema>;

export const addTagActionSchema = z.object({
  actionType: z.literal(RuleActionType.ADD_TAG),
  tag: z.array(z.string()),
});

export type AddTagAction = z.infer<typeof addTagActionSchema>;

export const removeTagActionSchema = z.object({
  actionType: z.literal(RuleActionType.REMOVE_TAG),
  tag: z.array(z.string()),
});

export type RemoveTagAction = z.infer<typeof removeTagActionSchema>;

export const autoReplyActionSchema = z.object({
  actionType: z.literal(RuleActionType.AUTO_REPLY),
  textContent: z.string(),
  htmlContent: z.string(),
  attachments: z
    .array(
      z.object({
        url: z.string(),
        description: z.string(),
        mimeType: z.string(),
      }),
    )
    .optional(),
});

export type AutoReplyAction = z.infer<typeof autoReplyActionSchema>;

export const assignActionSchema = z.object({
  actionType: z.literal(RuleActionType.ASSIGN),
  user: z.object({ id: z.string(), name: z.string() }),
});

export type AssignAction = z.infer<typeof assignActionSchema>;

export const assignV3ActionSchema = z.object({
  actionType: z.literal(RuleActionType.ASSIGN_V3),
  mode: z.discriminatedUnion("_tag", [
    z.object({
      _tag: z.literal("specific_users"),
      mongoUserIds: z.array(z.string()),
    }),
    z.object({ _tag: z.literal("user_group"), userGroupId: z.string() }),
    z.object({ _tag: z.literal("unassign") }),
    z.object({
      _tag: z.literal("actor"),
      assigneeTypes: z.array(z.enum(["USER", "AI"])).min(1),
    }),
  ]),
  strategy: createEnum(AssignV3Strategy),
  // Optional per-user open-ticket cap. Null / omitted = no cap.
  maxTicketsPerUser: z.number().int().positive().nullable().optional(),
});

export type AssignV3Action = z.infer<typeof assignV3ActionSchema>;

export const likeCommentActionSchema = z.object({
  actionType: z.literal(RuleActionType.LIKE_COMMENT),
});

export type LikeCommentAction = z.infer<typeof likeCommentActionSchema>;

export const hideCommentActionSchema = z.object({
  actionType: z.literal(RuleActionType.HIDE_COMMENT),
});

export type HideCommentAction = z.infer<typeof hideCommentActionSchema>;

export const setPriorityActionSchema = z.object({
  actionType: z.literal(RuleActionType.SET_PRIORITY),
  // `null` clears the priority. `"URGENT"` is currently the only set value.
  priority: z.enum(["URGENT"]).nullable(),
});

export type SetPriorityAction = z.infer<typeof setPriorityActionSchema>;

export const setStatusV3ActionSchema = z.object({
  actionType: z.literal(RuleActionType.SET_STATUS_V3),
  status: z.enum([
    "OPEN",
    "CLOSED",
    "SNOOZED",
    "IN_PROGRESS",
    "ARCHIVED",
    "DELETED",
  ]),
  snoozePeriod: createEnum(SetStatusSnoozePeriod).optional(),
});

export type SetStatusV3Action = z.infer<typeof setStatusV3ActionSchema>;

export const startAiAgentExecutionActionSchema = z.object({
  actionType: z.literal(RuleActionType.START_AI_AGENT_EXECUTION),
});

export type StartAiAgentExecutionAction = z.infer<
  typeof startAiAgentExecutionActionSchema
>;

export const handoffAiActionSchema = z.object({
  actionType: z.literal(RuleActionType.HANDOFF_AI),
});

export type HandoffAiAction = z.infer<typeof handoffAiActionSchema>;

export const generateConversationInsightsActionSchema = z.object({
  actionType: z.literal(RuleActionType.GENERATE_CONVERSATION_INSIGHTS),
});

export type GenerateConversationInsightsAction = z.infer<
  typeof generateConversationInsightsActionSchema
>;

export const sendCsatSurveyActionSchema = z.object({
  actionType: z.literal(RuleActionType.SEND_CSAT_SURVEY),
  surveySendTime: createEnum(SurveySendTimeOptions).optional(),
  surveyMethods: z.array(createEnum(CsatSurveyMethod)).optional(),
  allowRemarks: z.boolean().optional(),
  headerImage: z.string().optional(),
  surveyMessage: z.string().optional(),
});

export type SendCsatSurveyAction = z.infer<typeof sendCsatSurveyActionSchema>;

export const mergeSupportTicketActionSchema = z.object({
  actionType: z.literal(RuleActionType.MERGE_SUPPORT_TICKET),
  // Required — at least one status must be selected, otherwise no peer
  // ticket can ever match and the action is a guaranteed no-op.
  allowedStatuses: z.array(z.enum(CONVERSATION_STATUSES_V3)).min(1),
  maxAgeSinceLastMessage: z.object({
    value: z.number().int().positive(),
    unit: z.nativeEnum(TimeUnit),
  }),
});

export type MergeSupportTicketAction = z.infer<
  typeof mergeSupportTicketActionSchema
>;

export const splitSupportTicketActionSchema = z.object({
  actionType: z.literal(RuleActionType.SPLIT_SUPPORT_TICKET),
  routeFutureMessagesToNewTicket: z.boolean().default(true),
});

export type SplitSupportTicketAction = z.infer<
  typeof splitSupportTicketActionSchema
>;

export const ruleActionSchema = z.union([
  setStatusActionSchema,
  addTagActionSchema,
  removeTagActionSchema,
  autoReplyActionSchema,
  assignActionSchema,
  assignV3ActionSchema,
  likeCommentActionSchema,
  hideCommentActionSchema,
  setPriorityActionSchema,
  setStatusV3ActionSchema,
  startAiAgentExecutionActionSchema,
  handoffAiActionSchema,
  generateConversationInsightsActionSchema,
  sendCsatSurveyActionSchema,
  mergeSupportTicketActionSchema,
  splitSupportTicketActionSchema,
]);

export type RuleAction = z.infer<typeof ruleActionSchema>;

/* Rule Step Schemas */

const ruleConditionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_CONDITIONS),
  conditionGroups: conditionGroupSchema,
  nextTrueId: z.string().optional(),
  nextFalseId: z.string().optional(),
});
export const ruleConditionsStepSchema: z.ZodType<RuleConditionsStep> =
  ruleConditionsStepSchema_;
export interface RuleConditionsStep
  extends z.infer<typeof ruleConditionsStepSchema_> {}

const ruleActionsStepSchema_ = baseStepSchema.extend({
  type: z.literal(StepType.RULE_ACTIONS),
  actions: z.array(ruleActionSchema),
  errorHandlingStrategy: createEnum(ErrorHandlingStrategy).default(
    ErrorHandlingStrategy.FAIL_FAST,
  ),
  nextId: z.string().optional(),
});
export const ruleActionsStepSchema: z.ZodType<RuleActionsStep> =
  ruleActionsStepSchema_;
export interface RuleActionsStep
  extends z.infer<typeof ruleActionsStepSchema_> {}

/* Rule Execution Metadata Schemas */

export const conditionResultSchema = z.object({
  conditionType: createEnum(RuleConditionType),
  passed: z.boolean(),
  evaluatedAt: DateSchema,
  error: z.string().optional(),
});

export type ConditionResult = z.infer<typeof conditionResultSchema>;

export const actionResultSchema = z.object({
  actionType: createEnum(RuleActionType),
  status: z.enum(["success", "failed", "skipped"]),
  executedAt: DateSchema,
  error: z.string().optional(),
});

export type ActionResult = z.infer<typeof actionResultSchema>;

export const ruleExecutionMetadataSchema = z.object({
  trigger: triggerMetadataSchema,
  conditionResults: z.array(conditionResultSchema).optional(),
  actionResults: z.array(actionResultSchema).optional(),
  executionId: z.string(),
  startedAt: DateSchema,
  completedAt: DateSchema.optional(),
  status: z.enum(["success", "failed", "partial"]),
});

export type RuleExecutionMetadata = z.infer<typeof ruleExecutionMetadataSchema>;
